import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Sprite,
  SparkleBurst,
  PromptCapsule,
  FloatingHearts,
  StarPips,
  JuicyMeter,
  CaptionChip,
  AmbientLayer,
} from "./fx";

// BeeStill — the statue game. The bee wanders in on a looping, curious flight
// while the child DOESN'T touch: stillness is the whole mechanic. Any tap
// startles her — she buzzes back a little and waits — so the skill being
// practiced is calm hands. The reward flips the tension: once she lands on a
// flower, the child earns ONE gentle pat before she lifts off for the next
// bloom. After the last visit she flies happily home.

const VISIT_SECONDS = 4.5; // one full approach, untouched
const STARTLE_SECONDS = 1.0; // how long she shakes before trying again
const STARTLE_SETBACK = 0.18; // how much of the journey a startle costs
const LAND_PAUSE_MS = 2600; // the hello on the flower (long enough to pat her)

// Where the bee starts each visit (fractions of the stage box), alternating
// sides so every visit reads fresh.
const STARTS = [
  { x: 0.08, y: 0.16 },
  { x: 0.92, y: 0.1 },
  { x: 0.06, y: 0.06 },
  { x: 0.94, y: 0.2 },
];

const FLOWER_Y = 0.76;
const FLOWER_ASSETS = [
  "ch_flower_bloom_yellow",
  "ch_flower_bloom_purple",
  "ch_flower_bloom",
  "ch_flower_bloom_orange",
];

const NUDGES = [
  "Bzz! She got startled. Statue still…",
  "Whoops — hold extra still. Here she comes again…",
  "Soft and still… she's almost there.",
];

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const BeeStill = ({ payload, sounds, onDone }) => {
  const totalVisits = payload.visits || payload.breaths || 2;
  const flowerX = useCallback(
    (i) => 0.5 + (i - (totalVisits - 1) / 2) * (totalVisits > 2 ? 0.26 : 0.3),
    [totalVisits]
  );

  const [progress, setProgress] = useState(0);
  const [visitsDone, setVisitsDone] = useState(0);
  const [beePhase, setBeePhase] = useState("approach"); // approach | startled | landed | home
  const [wobble, setWobble] = useState(0);
  const [sparkle, setSparkle] = useState(0);
  const [hearts, setHearts] = useState(0);
  const [petHearts, setPetHearts] = useState(0);
  const [petted, setPetted] = useState(false);
  const [label, setLabel] = useState("Statue mode! Keep your hands still…");
  const [labelTone, setLabelTone] = useState("active");

  const progressRef = useRef(0);
  const visitsRef = useRef(0);
  const phaseRef = useRef("approach");
  const startledUntilRef = useRef(0);
  const nudgeRef = useRef(0);
  const doneRef = useRef(false);
  const rafRef = useRef(0);
  const lastTsRef = useRef(0);
  const trailRef = useRef([]);
  const frameRef = useRef(0);
  const tiltRef = useRef(0);
  const lastXRef = useRef(null);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    phaseRef.current = "home";
    setBeePhase("home");
    sounds.playSparkle();
    setSparkle((s) => s + 1);
    setHearts((h) => h + 1);
    setLabel("She's buzzing home, happy as can be!");
    setLabelTone("done");
    sounds.speakSoothing("She loved meeting you. Being still is a superpower!");
    window.setTimeout(() => onDone(), 1800);
  }, [onDone, sounds]);

  const land = useCallback(() => {
    phaseRef.current = "landed";
    setBeePhase("landed");
    trailRef.current = [];
    visitsRef.current += 1;
    setVisitsDone(visitsRef.current);
    setPetted(false);
    sounds.playPop(0.7);
    sounds.playSparkle();
    setSparkle((s) => s + 1);
    setHearts((h) => h + 1);

    if (visitsRef.current >= totalVisits) {
      setLabel("She landed! Give her one gentle pat…");
      setLabelTone("active");
      window.setTimeout(finish, LAND_PAUSE_MS);
      return;
    }
    setLabel("She landed! Give her one gentle pat…");
    setLabelTone("active");
    window.setTimeout(() => {
      if (doneRef.current) return;
      progressRef.current = 0;
      setProgress(0);
      lastXRef.current = null;
      phaseRef.current = "approach";
      setBeePhase("approach");
      setLabel("Statue mode! Keep your hands still…");
      setLabelTone("active");
      sounds.speakSoothing("Here she comes again. Hold still…");
    }, LAND_PAUSE_MS);
  }, [finish, sounds, totalVisits]);

  useEffect(() => {
    const tick = (ts) => {
      const last = lastTsRef.current || ts;
      const dt = Math.min(0.05, (ts - last) / 1000);
      lastTsRef.current = ts;

      if (!doneRef.current) {
        if (phaseRef.current === "startled" && ts >= startledUntilRef.current) {
          phaseRef.current = "approach";
          setBeePhase("approach");
          setLabel("Statue mode! Keep your hands still…");
          setLabelTone("active");
        }
        if (phaseRef.current === "approach") {
          const next = Math.min(1, progressRef.current + dt / VISIT_SECONDS);
          progressRef.current = next;
          setProgress(next);
          if (next >= 1) land();
        }
      }
      rafRef.current = window.requestAnimationFrame(tick);
    };
    rafRef.current = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafRef.current);
  }, [land]);

  // ANY touch on the stage startles her — that's the game.
  const handleStartle = useCallback(() => {
    if (doneRef.current || phaseRef.current === "landed" || phaseRef.current === "home") return;
    progressRef.current = Math.max(0, progressRef.current - STARTLE_SETBACK);
    setProgress(progressRef.current);
    phaseRef.current = "startled";
    setBeePhase("startled");
    startledUntilRef.current = performance.now() + STARTLE_SECONDS * 1000;
    setWobble((w) => w + 1);
    sounds.playThud();
    const nudge = NUDGES[nudgeRef.current % NUDGES.length];
    nudgeRef.current += 1;
    setLabel(nudge);
    setLabelTone("oops");
    sounds.speakSoothing("Bzz! Hold statue still.");
  }, [sounds]);

  // The earned touch: one soft pat while she rests on the flower.
  const handlePet = useCallback(
    (event) => {
      event.stopPropagation();
      if (phaseRef.current !== "landed" || petted) return;
      setPetted(true);
      setPetHearts((h) => h + 1);
      setSparkle((s) => s + 1);
      sounds.playSparkle();
      setLabel("She loved that! Soft hands win her heart.");
      setLabelTone("done");
      sounds.speakSoothing("Hee hee, she loves it!");
    },
    [petted, sounds]
  );

  // ----- the bee's spot: a curious, looping wander toward this visit's flower

  const landedOrLater = beePhase === "landed" || beePhase === "home";
  const activeIndex = landedOrLater
    ? Math.max(0, visitsDone - 1)
    : Math.min(visitsDone, totalVisits - 1);
  const start = STARTS[activeIndex % STARTS.length];
  const targetX = flowerX(activeIndex);

  const eased = 0.5 - 0.5 * Math.cos(Math.min(1, progress) * Math.PI);
  const t = landedOrLater ? 1 : eased;
  const fade = 1 - t; // wandering settles as she nears the flower
  const arc = Math.sin(t * Math.PI) * -0.14;
  const wanderX = Math.sin(t * Math.PI * 3 + activeIndex * 1.7) * 0.07 * fade;
  const wanderY = Math.sin(t * Math.PI * 4.3 + activeIndex * 2.3) * 0.05 * fade;
  const beeX = start.x + (targetX - start.x) * t + wanderX;
  const beeY =
    start.y + (FLOWER_Y - start.y) * t + arc + wanderY + (landedOrLater ? -0.055 : 0);

  // Lean into the direction of travel, smoothed so she banks like a real bug.
  if (beePhase === "approach") {
    const lastX = lastXRef.current;
    if (lastX != null) {
      const target = clamp((beeX - lastX) * 2600, -22, 22);
      tiltRef.current = tiltRef.current * 0.82 + target * 0.18;
    }
    lastXRef.current = beeX;
    frameRef.current += 1;
    if (frameRef.current % 4 === 0) {
      trailRef.current = [...trailRef.current.slice(-8), { x: beeX, y: beeY }];
    }
  } else if (landedOrLater) {
    tiltRef.current = 0;
  }
  const trail = beePhase === "approach" ? trailRef.current : [];

  return (
    <div className="ch-activity-body ch-bee-still">
      <PromptCapsule text={payload.prompt} sounds={sounds} />

      <StarPips total={totalVisits} done={visitsDone} />

      <div
        className="ch-bee-stage"
        onPointerDown={handleStartle}
        style={{ touchAction: "none" }}
      >
        <AmbientLayer variant="day" />
        <span className="ch-bee-sun" aria-hidden="true"><Sprite name="ch_zone_sun" /></span>
        <div className="ch-bee-grass" aria-hidden="true" />

        {/* Fading flight trail — the wandering path made visible. */}
        {trail.map((p, i) => (
          <span
            key={i}
            className="ch-bee-trail-dot"
            style={{
              left: `${p.x * 100}%`,
              top: `${p.y * 100}%`,
              opacity: ((i + 1) / trail.length) * 0.5,
              transform: `translate(-50%, -50%) scale(${0.4 + (0.6 * (i + 1)) / trail.length})`,
            }}
            aria-hidden="true"
          />
        ))}

        {/* One flower per visit; each stays glowing once she's said hello. */}
        {Array.from({ length: totalVisits }, (_, i) => {
          const visited = i < visitsDone;
          const isTarget = !landedOrLater && i === activeIndex && !doneRef.current;
          return (
            <div
              key={i}
              className={`ch-bee-flower ${visited ? "is-visited" : ""} ${
                isTarget ? "is-target" : ""
              }`}
              style={{ left: `${flowerX(i) * 100}%` }}
            >
              {visited && <span className="ch-bee-flower-glow" />}
              <Sprite name={FLOWER_ASSETS[i % FLOWER_ASSETS.length]} />
              {i === activeIndex && (
                <>
                  <SparkleBurst trigger={sparkle} reach={90} style={{ left: "50%", top: "8%" }} />
                  <FloatingHearts trigger={hearts} style={{ left: "50%", top: "-2%" }} />
                </>
              )}
            </div>
          );
        })}

        <div
          className={`ch-bee-flyer ${beePhase === "startled" ? "is-startled" : ""} ${
            beePhase === "home" ? "is-home" : ""
          } ${beePhase === "approach" ? "is-bobbing" : ""} ${
            beePhase === "landed" ? "is-landed" : ""
          } ${beePhase === "landed" && !petted ? "is-pettable" : ""} ${
            petted && beePhase === "landed" ? "is-petted" : ""
          }`}
          key={`wobble-${wobble}`}
          style={{
            left: `${beeX * 100}%`,
            top: `${beeY * 100}%`,
            "--ch-bee-tilt": `${tiltRef.current.toFixed(1)}deg`,
          }}
          onPointerDown={beePhase === "landed" ? handlePet : undefined}
          role={beePhase === "landed" && !petted ? "button" : undefined}
          aria-label={beePhase === "landed" && !petted ? "Give the bee a gentle pat" : undefined}
        >
          <Sprite name={payload.companionAsset || "ch_bee"} />
          <FloatingHearts trigger={petHearts} style={{ left: "50%", top: "-10%" }} />
        </div>

        {/* Startle puff — a quick "Bzz!" right where she jumped. */}
        <AnimatePresence>
          {beePhase === "startled" && (
            <motion.span
              key={wobble}
              className="ch-bee-bubble"
              style={{ left: `${beeX * 100}%`, top: `${(beeY - 0.12) * 100}%` }}
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 18 }}
            >
              Bzz!
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <JuicyMeter
        progress={landedOrLater ? 1 : progress}
        tint="#f5aa21"
        icon={<Sprite name={payload.companionAsset || "ch_bee"} />}
        full={beePhase === "home"}
      />

      <CaptionChip tone={labelTone} shakeKey={labelTone === "oops" ? wobble : undefined}>
        {label}
      </CaptionChip>
    </div>
  );
};

export default BeeStill;
